import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Star } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AdminTitle, Modal, Field } from "@/pages/admin/AdminUI";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";

const empty = { name: "", company: "", country: "", rating: 5, text: "", photo: "", service_used: "", status: "Published" };
const STATUS = ["Draft", "Pending", "Published", "Hidden"];
const badge = { Draft: "text-slate-400", Pending: "text-amber-400", Published: "text-emerald-400", Hidden: "text-slate-500" };

export default function AdminTestimonials() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...empty, ...t }); setOpen(true); };
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { const payload = { ...form, rating: +form.rating }; if (editing) await api.put(`/admin/testimonials/${editing.id}`, payload); else await api.post("/admin/testimonials", payload); toast.success("Saved"); setOpen(false); load(); }
    catch (err) { toast.error(apiError(err)); }
    setSaving(false);
  };
  const del = async (t) => { if (!window.confirm("Delete testimonial?")) return; await api.delete(`/admin/testimonials/${t.id}`); load(); };

  if (items === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-400" size={36} /></div>;

  return (
    <div>
      <AdminTitle title="Testimonials" action={<button onClick={openNew} className="gold-btn text-sm" data-testid="admin-add-testimonial"><Plus size={16} /> Add Testimonial</button>} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t) => (
          <div key={t.id} className="card-lux p-5" data-testid={`admin-testimonial-${t.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-white text-sm">{t.name}</span>
              <span className={`text-xs font-semibold ${badge[t.status]}`}>{t.status}</span>
            </div>
            <div className="flex gap-0.5 mb-2">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={12} className={i < t.rating ? "text-amber-400 fill-amber-400" : "text-slate-600"} />)}</div>
            <p className="text-xs text-slate-400 line-clamp-3">{t.text}</p>
            <div className="flex gap-3 mt-3">
              <button onClick={() => openEdit(t)} className="text-slate-400 hover:text-amber-400" data-testid={`edit-testimonial-${t.id}`}><Pencil size={15} /></button>
              <button onClick={() => del(t)} className="text-slate-400 hover:text-red-400"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Testimonial" : "New Testimonial"}>
        <form onSubmit={save} className="space-y-4" data-testid="testimonial-admin-form">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Customer Name"><input required className="input-lux" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tm-name-field" /></Field>
            <Field label="Company"><input className="input-lux" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Country"><input className="input-lux" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
            <Field label="Rating"><input type="number" min="1" max="5" className="input-lux" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} /></Field>
            <Field label="Status"><select className="input-lux" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="tm-status-field">{STATUS.map((s) => <option key={s}>{s}</option>)}</select></Field>
          </div>
          <ImageUpload label="Customer Photo" value={form.photo} onChange={(v) => setForm({ ...form, photo: v })} testid="tm-photo" />
          <Field label="Service Used"><input className="input-lux" value={form.service_used} onChange={(e) => setForm({ ...form, service_used: e.target.value })} /></Field>
          <Field label="Testimonial Text"><textarea required rows={4} className="input-lux resize-none" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="tm-text-field" /></Field>
          <button disabled={saving} className="gold-btn w-full" data-testid="tm-save-btn">{saving ? <Loader2 className="animate-spin" size={18} /> : "Save"}</button>
        </form>
      </Modal>
    </div>
  );
}
